import React, { useEffect, useRef, useState } from "react";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridFilterChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { EDIT_ACCESS, PRINT_ACCESS } from "../../_contstants/common";
import { checkAcessRights } from "../../_helper/helper";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import {
  clearFilterOrderLabeling,
  setFilterOrderLabeling,
} from "./orderLabelingSlice";
import { findAllFinishedOrder } from "./services/orderLabeling.services";

const OrderLabeling: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(
    undefined,
  );

  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const orderList = useAppSelector(
    (state) => state.orderLabeling.finishedOrderList,
  );
  const loading = useAppSelector((state) => state.orderLabeling.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterOrder = useAppSelector((state) => state.orderLabeling.filter);

  useEffect(() => {
    const payload = {
      ...FilterOrder,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(findAllFinishedOrder(payload));
  }, [currentPage, pageLimit, dispatch, FilterOrder]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 6 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className="table-cell">
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyEditCommandCell = (props: any) => {
    const handleViewLabelItem = (ID: number) => {
      navigate("generate", {
        state: { ID },
      });
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Generate Label"
            themeColor="tertiary"
            onClick={() => handleViewLabelItem(props.dataItem.id)}
          >
            <FaEye className="absolute-position" style={{ fontSize: "24px" }} />
          </IconButton>
        )}
      </td>
    );
  };

  const OrderFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        numberfiels={["order_no"]}
        filterSelector={(state) => state.orderLabeling.filter}
        setFilterAction={setFilterOrderLabeling}
        clearFilterAction={clearFilterOrderLabeling}
        fetchDataAction={findAllFinishedOrder}
        pagination={{ case: "lowercase" }}
      />
    ),
    [],
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Generate Labels
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <KendoGrid
            scrollable="scrollable"
            className="responsive-table"
            style={{ maxHeight: "calc(100vh - 152px)" }}
            filterable={true}
            filter={filter}
            data={filter ? filterBy(orderList, filter) : orderList}
            total={orderList[0]?.itemcount || orderList.length}
            onFilterChange={handleFilterChange}
            skip={currentPage * pageLimit}
            take={pageLimit}
            pageable={{
              buttonCount: 5,
              pageSizes: [10, 50, 100, 300, 500],
              pageSizeValue: pageLimit,
              type: "input",
            }}
            onPageChange={pageChange}
            cells={{
              headerCell: HeaderCustomCell,
              data: MyDataCustomCell,
            }}
          >
            <Column
              field="order_no"
              title="Order No"
              width={100}
              cells={{
                data: (props: any) => (
                  <td>
                    <div style={{ color: "green" }}>
                      <strong>{props.dataItem?.order_no}</strong>
                    </div>
                  </td>
                ),
                filterCell: OrderFilterCell,
              }}
            />
            <Column
              field="order_date"
              title="Order Date"
              width={130}
              cells={{ filterCell: OrderFilterCell }}
            />
            <Column
              field="delivery_date"
              title="Delivery Date"
              width={130}
              cells={{ filterCell: OrderFilterCell }}
            />
            <Column
              field="item_name"
              title="Product Name"
              width={200}
              cells={{ filterCell: OrderFilterCell }}
            />
            <Column
              field="party_name"
              title="Party Name"
              width={200}
              cells={{ filterCell: OrderFilterCell }}
            />
            {/* <Column
              field="purity"
              title="Purity"
              width={150}
              cells={{ filterCell: OrderFilterCell }}
            /> */}
            {/* <Column
              field="GrossWeight"
              title="Gross Wt (gm)"
              width={120}
              format="{0:n3}"
            />
            <Column
              field="NetWeight"
              title="Net Wt (gm)"
              width={120}
              format="{0:n3}"
            /> */}
            {isEditAccess && isPrintAccess && (
              <Column
                field="id"
                title="Actions"
                cells={{ data: MyEditCommandCell }}
                width={"100px"}
                filterable={false}
                locked={true}
              />
            )}
          </KendoGrid>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default OrderLabeling;
